import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import MegaMenuColumn from './MegaMenuColumn';
import useMegaMenuDismiss from '../hooks/useMegaMenuDismiss';
import useMegaMenuPosition from '../hooks/useMegaMenuPosition';

const MegaMenu = ({ item, visible = true, onClose, triggerRef }) => {
	const menuRef = useRef(null);
	const topPx = useMegaMenuPosition(menuRef, visible);
	const { onMouseLeave } = useMegaMenuDismiss({
		menuRef,
		onClose,
		visible,
		triggerRef
	});

	const columns = item.columns || [];
	if (columns.length === 0) return null;

	const gridTemplateColumns = columns
		.map((c) => `minmax(0, ${c.gridUnits}fr)`)
		.join(' ');

	return (
		<>
			{visible && (
				<>
					{/* Full-viewport scrim below the header. Besides the usual
					dim-the-page-behind-the-flyout treatment, its opaque
					background also covers the same scrollbar-width gap over
					normal page content (e.g. a hero banner) for the rest of
					the viewport.
					Height is computed via 100vh rather than a CSS `bottom: 0`
					constraint -- some host pages give position:fixed elements
					here a containing block shorter than the real viewport
					(same category of CMS-scaffolding quirk as the position
					fix below), which silently zeroed out a bottom-anchored
					height. 100vh always resolves against the true viewport
					regardless of containing block, matching how `maxHeight`
					below is computed. */}
					<div
						className="ws-nav-drawer__mega-menu-backdrop"
						style={{
							top: `${topPx}px`,
							height: `calc(100vh - ${topPx}px)`
						}}
						aria-hidden="true"
					/>
				</>
			)}
			<div
				id={`mega-menu-${item.id}`}
				ref={menuRef}
				className="ws-nav-drawer__mega-menu"
				style={{
					top: `${topPx}px`,
					display: visible ? '' : 'none',
					maxHeight: `calc(100vh - ${topPx}px)`,
					overflowY: 'auto'
				}}
				aria-hidden={!visible}
				onMouseLeave={onMouseLeave}
			>
				<div
					className="ws-nav-drawer__mega-menu__content"
					role="region"
					aria-label={item.label}
					style={{ gridTemplateColumns }}
				>
					{columns.map((column, idx) => (
						<MegaMenuColumn
							// Index-as-key is safe here: columns are always
							// rendered directly from their parent item's own
							// `columns` array in a fixed order and are never
							// independently reordered or filtered. The prior
							// gridUnits+firstItemId key could collide between
							// two columns with the same gridUnits and no nav
							// items.
							// eslint-disable-next-line react/no-array-index-key
							key={idx}
							column={column}
							parentLabel={item.label}
							onLinkClick={onClose}
						/>
					))}
				</div>
			</div>
		</>
	);
};

MegaMenu.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		columns: PropTypes.arrayOf(
			PropTypes.shape({
				gridUnits: PropTypes.number.isRequired,
				navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
			})
		)
	}).isRequired,
	visible: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

MegaMenu.defaultProps = {
	visible: true,
	triggerRef: null
};

export default MegaMenu;
